import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { useDebouncedSave } from '@/lib/useDebouncedSave'
import { toast } from 'sonner'
import { format } from 'date-fns'
import {
  ChevronLeft, ChevronRight, ArrowLeft, Calendar, Pencil,
  Loader2, MoreHorizontal, Trash2, Clock, User as UserIcon
} from 'lucide-react'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar as CalendarPicker } from '@/components/ui/calendar'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { taskService } from '@/services/taskService'
import { projectService } from '@/services/projectService'
import { globalTaskService } from '@/services/globalTaskService'
import PriorityBadge from '@/components/ProjectCentral/PriorityBadge'
import RichTextEditor from '@/components/ProjectCentral/RichTextEditor'
import TaskComments from '@/components/ProjectCentral/TaskComments'
import TaskAttachments from '@/components/ProjectCentral/TaskAttachments'
import TaskActivityTimeline from '@/components/ProjectCentral/TaskActivityTimeline'
import TimeTracker from '@/components/ProjectCentral/TimeTracker'
import TaskDisplayId from '@/components/ProjectCentral/TaskDisplayId'
import TaskTypeSwitcher from '@/components/ProjectCentral/TaskTypeSwitcher'
import TaskWatchButton from '@/components/ProjectCentral/TaskWatchButton'
import { useHotkeys } from '@/lib/useKeyboardShortcuts'
import TaskTypeIcon from '@/components/ProjectCentral/TaskTypeIcon'
import ManualTimeEntryForm from '@/components/ProjectCentral/ManualTimeEntryForm'
import EditTimeEntryForm from '@/components/ProjectCentral/EditTimeEntryForm'
import { timeEntryService } from '@/services/timeEntryService'
import ProjectLayout from '@/components/ProjectCentral/ProjectLayout'
import AcceptanceCriteria from '@/components/ProjectCentral/AcceptanceCriteria'
import TaskLinksPanel from '@/components/ProjectCentral/TaskLinksPanel'
import LabelsInput from '@/components/ProjectCentral/LabelsInput'
import { ParentField, ChildItems } from '@/components/ProjectCentral/TaskHierarchy'
import { SprintField, EstimateFields, EstimateProgress } from '@/components/ProjectCentral/TaskPlanningFields'
import api from '@/services/api'

const PRIORITIES = ['low', 'medium', 'high', 'critical']

const formatDuration = (seconds) => {
  if (!seconds) return '0m'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

const TaskPage = () => {
  const { id: projectId, taskId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [descEditorKey, setDescEditorKey] = useState(0)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [manualEntryOpen, setManualEntryOpen] = useState(false)
  const [activityTab, setActivityTab] = useState('all')
  const [editingEntryId, setEditingEntryId] = useState(null)

  // Fetch task data
  const { data: taskData, isLoading } = useQuery(
    ['projects', String(projectId), 'tasks', String(taskId)],
    () => taskService.getTask(projectId, taskId),
    { enabled: !!taskId }
  )

  // Fetch statuses for dropdown
  const { data: statusData } = useQuery(
    ['projects', String(projectId), 'statuses'],
    () => projectService.getStatuses(projectId),
    { enabled: !!projectId }
  )

  // Fetch users for assignee dropdown
  const { data: usersData } = useQuery(
    'team-members',
    () => globalTaskService.getTeamMembers()
  )

  // Fetch task list for prev/next navigation
  const { data: taskListData } = useQuery(
    ['projects', String(projectId), 'tasks', { _nav: true }],
    () => taskService.getTasks(projectId, {}),
    { enabled: !!projectId, staleTime: 30000 }
  )

  const task = taskData?.data?.task
  const statuses = statusData?.data?.statuses || []
  const users = usersData?.data?.users || []
  const allTasks = taskListData?.data?.tasks || []

  // Build mention users list for the rich text editor
  const mentionUsers = React.useMemo(() =>
    users.map(u => ({
      id: String(u.id),
      label: u.employee?.first_name
        ? `${u.employee.first_name} ${u.employee.last_name}`
        : u.email,
    })),
    [users]
  )

  // Prev/Next navigation
  const currentIndex = allTasks.findIndex(t => t.id === parseInt(taskId))
  const prevTask = currentIndex > 0 ? allTasks[currentIndex - 1] : null
  const nextTask = currentIndex >= 0 && currentIndex < allTasks.length - 1 ? allTasks[currentIndex + 1] : null

  const displayId = task?.project?.key && task?.task_number
    ? `${task.project.key}-${task.task_number}`
    : null

  useEffect(() => {
    if (task) setTitleValue(task.title)
  }, [task?.id])

  // Related tasks (parent, children, links) may live in another project
  const openTask = (t) => navigate(`/projects/${t.project_id || projectId}/tasks/${t.id}`)

  const updateField = async (field, value) => {
    try {
      await taskService.updateTask(projectId, taskId, { [field]: value })
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to update ${field}`)
    }
  }

  // The editor reports every keystroke; save (and sync to Jira) once typing pauses
  const saveDescription = useDebouncedSave((html) => updateField('description', html))

  // Task shortcuts (the full list opens with "?")
  useHotkeys({
    j: () => nextTask && navigate(`/projects/${projectId}/tasks/${nextTask.id}`),
    k: () => prevTask && navigate(`/projects/${projectId}/tasks/${prevTask.id}`),
    a: () => task && user && task.assignee_id !== user.id && updateField('assignee_id', user.id),
    m: () => document.querySelector('[data-shortcut="comment"] .ProseMirror')?.focus(),
    s: () => document.querySelector('[data-shortcut="timer"] button')?.click()
  }, { enabled: !!task })

  const handleTitleSave = async () => {
    if (!titleValue.trim()) return
    if (titleValue.trim() === task?.title) { setEditingTitle(false); return }
    await updateField('title', titleValue.trim())
    setEditingTitle(false)
  }

  const handleDelete = async () => {
    try {
      await taskService.deleteTask(projectId, taskId)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      navigate(`/projects/${projectId}/board`)
      toast.success('Task deleted')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete task')
    }
  }

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(`/projects/${projectId}/board`)
    }
  }

  const getUserName = (u) => {
    if (u?.employee?.first_name) return `${u.employee.first_name} ${u.employee.last_name}`
    return u?.email || 'Unknown'
  }

  if (isLoading) {
    return (
      <ProjectLayout>
        <div className="p-6 space-y-4 max-w-6xl mx-auto">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-32 w-full" />
            </div>
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  if (!task) {
    return (
      <ProjectLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="font-medium">Task not found</p>
          <Button variant="link" onClick={() => navigate(`/projects/${projectId}/board`)}>
            Back to Board
          </Button>
        </div>
      </ProjectLayout>
    )
  }

  const creatorName = getUserName(task.creator)

  return (
    <ProjectLayout>
      <div className="max-w-6xl mx-auto">
        {/* Top bar: Back, Display ID, Prev/Next, Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <span className="inline-flex items-center gap-1.5">
              <TaskTypeSwitcher taskId={task?.id} projectId={projectId} projectKey={task?.project?.key} currentType={task?.type}>
                <button className="hover:opacity-70 transition-opacity cursor-pointer" title="Change task type">
                  <TaskTypeIcon type={task?.type} size="md" />
                </button>
              </TaskTypeSwitcher>
              <TaskDisplayId projectId={projectId} projectKey={task?.project?.key} taskId={taskId} taskNumber={task?.task_number} size="md" />
            </span>
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost" size="icon" className="h-7 w-7"
                disabled={!prevTask}
                onClick={() => prevTask && navigate(`/projects/${projectId}/tasks/${prevTask.id}`)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="icon" className="h-7 w-7"
                disabled={!nextTask}
                onClick={() => nextTask && navigate(`/projects/${projectId}/tasks/${nextTask.id}`)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {task && <TaskWatchButton projectId={projectId} taskId={task.id} className="h-8 gap-1.5 px-2.5 text-muted-foreground" />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setDeleteOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete task
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* LEFT PANEL (60%) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Title */}
            <div>
              {editingTitle ? (
                <Input
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave()
                    if (e.key === 'Escape') { setTitleValue(task.title); setEditingTitle(false) }
                  }}
                  className="text-2xl font-semibold h-auto py-1.5 px-2 border-primary"
                  autoFocus
                />
              ) : (
                <div
                  className="group flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-2 py-1.5 -mx-2 transition-colors"
                  onClick={() => setEditingTitle(true)}
                >
                  <h1 className="text-2xl font-semibold">{task.title}</h1>
                  <Pencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
              <RichTextEditor
                key={`desc-${task.id}-${descEditorKey}`}
                content={task.description || ''}
                onChange={saveDescription}
                placeholder="Add a description..."
                minHeight={120}
                mentionUsers={mentionUsers}
                onImageUpload={async (file) => {
                  const res = await taskService.uploadAttachments(projectId, task.id, [file])
                  const att = res?.data?.attachments?.[0]
                  if (att) {
                    queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(task.id)])
                    return att.preview_url // signed URL; stored as the stable /uploads path on save
                  }
                }}
              />
            </div>

            <AcceptanceCriteria task={task} projectId={projectId} mentionUsers={mentionUsers} />

            <Separator />

            <ChildItems task={task} projectId={projectId} projectKey={task.project?.key} onOpenTask={openTask} />
            <TaskLinksPanel task={task} projectId={projectId} onOpenTask={openTask} />

            <Separator />

            {/* Attachments */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">
                Attachments {task.attachments?.length > 0 && <span className="text-muted-foreground">({task.attachments.length})</span>}
              </h3>
              <TaskAttachments
                taskId={task.id}
                projectId={projectId}
                attachments={task.attachments || []}
              />
            </div>

            <Separator />

            {/* Activity with Tabs */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Activity</h3>
              <Tabs value={activityTab} onValueChange={setActivityTab}>
                <TabsList className="h-8">
                  <TabsTrigger value="all" className="text-xs h-7">All</TabsTrigger>
                  <TabsTrigger value="comments" className="text-xs h-7">Comments</TabsTrigger>
                  <TabsTrigger value="history" className="text-xs h-7">History</TabsTrigger>
                  <TabsTrigger value="time" className="text-xs h-7">Time entries</TabsTrigger>
                </TabsList>
              </Tabs>
              <div data-shortcut="comment">
                <TaskComments taskId={task.id} projectId={projectId} mentionUsers={mentionUsers} />
              </div>
              <TaskActivityTimeline
                activities={task.activities || []}
                comments={task.comments || []}
                timeEntries={task.timeEntries || []}
                filter={activityTab}
                projectId={projectId}
                taskId={task.id}
                mentionUsers={mentionUsers}
                currentUserId={user?.id}
              />
            </div>
          </div>

          {/* RIGHT PANEL (40%) */}
          <div className="lg:col-span-2">
            <div className="sticky top-20 space-y-5">
              {/* Status */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground font-medium">Status</label>
                <Select
                  value={String(task.status_id)}
                  onValueChange={(v) => updateField('status_id', parseInt(v))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            'w-2 h-2 rounded-full',
                            s.category === 'done' ? 'bg-green-500' :
                            s.category === 'in_progress' ? 'bg-blue-500' : 'bg-slate-400'
                          )} />
                          {s.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Assignee */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground font-medium">Assignee</label>
                <Select
                  value={task.assignee_id ? String(task.assignee_id) : 'unassigned'}
                  onValueChange={(v) => updateField('assignee_id', v === 'unassigned' ? null : parseInt(v))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{getUserName(u)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {task.assignee_id !== user?.id && (
                  <button
                    className="text-xs text-primary hover:underline"
                    onClick={() => updateField('assignee_id', user?.id)}
                  >
                    Assign to me
                  </button>
                )}
              </div>

              {/* Due Date */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground font-medium">Due date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 w-full justify-start font-normal">
                      <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                      {task.due_date
                        ? format(new Date(task.due_date + 'T00:00:00'), 'MMM d, yyyy')
                        : <span className="text-muted-foreground">None</span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={task.due_date ? new Date(task.due_date + 'T00:00:00') : undefined}
                      onSelect={(date) => updateField('due_date', date ? format(date, 'yyyy-MM-dd') : null)}
                      initialFocus
                    />
                    {task.due_date && (
                      <div className="p-2 border-t">
                        <Button
                          variant="ghost" size="sm" className="w-full text-muted-foreground"
                          onClick={() => updateField('due_date', null)}
                        >
                          Clear date
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Priority */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground font-medium">Priority</label>
                <Select
                  value={task.priority}
                  onValueChange={(v) => updateField('priority', v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>
                        <PriorityBadge priority={p} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Labels */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground font-medium">Labels</label>
                <LabelsInput value={task.labels || []} onChange={(labels) => updateField('labels', labels)} projectId={projectId} />
              </div>

              {/* Epic / parent */}
              <ParentField task={task} projectId={projectId} projectKey={task.project?.key} onOpenTask={openTask} className="space-y-1.5" labelClassName="text-xs text-muted-foreground font-medium" />

              {/* Sprint + estimates */}
              <SprintField task={task} projectId={projectId} onUpdate={updateField} />
              <EstimateFields task={task} onUpdate={updateField} />

              {/* Reporter */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground font-medium">Reporter</label>
                <div className="flex items-center gap-2 px-1">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                      {creatorName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{creatorName}</span>
                </div>
              </div>

              <Separator />

              {/* Time Tracking */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">
                    Time Tracking
                    {task.totalTimeLogged > 0 && (
                      <span className="text-muted-foreground ml-1">
                        — {formatDuration(task.totalTimeLogged)}
                      </span>
                    )}
                  </h3>
                </div>
                <EstimateProgress estimate={task.original_estimate_seconds} logged={task.totalTimeLogged || 0} />
                <div className="flex items-center gap-2" data-shortcut="timer">
                  <TimeTracker
                    taskId={task.id}
                    projectId={projectId}
                    taskTitle={task.title}
                  />
                  <Button
                    size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => setManualEntryOpen(v => !v)}
                  >
                    + Manual
                  </Button>
                </div>
                {manualEntryOpen && (
                  <ManualTimeEntryForm
                    taskId={task.id}
                    projectId={projectId}
                    onClose={() => setManualEntryOpen(false)}
                    existingEntries={task.timeEntries?.filter(e => e.user_id === user?.id) || []}
                  />
                )}
                {task.timeEntries?.filter(t => t.end_time).length > 0 && (
                  <div className="space-y-1">
                    {task.timeEntries.filter(t => t.end_time).slice(0, 5).map(entry => {
                      const userName = getUserName(entry.user)
                      const isOwn = entry.user_id === user?.id

                      if (editingEntryId === entry.id) {
                        return (
                          <EditTimeEntryForm
                            key={entry.id}
                            entry={entry}
                            taskId={task.id}
                            projectId={projectId}
                            onClose={() => setEditingEntryId(null)}
                            existingEntries={task.timeEntries?.filter(e => e.user_id === user?.id) || []}
                          />
                        )
                      }

                      return (
                        <div key={entry.id} className="group flex items-center gap-2 text-sm text-muted-foreground py-0.5">
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium text-foreground">{formatDuration(entry.duration)}</span>
                          <span>·</span>
                          <span className="truncate">{userName}</span>
                          {entry.note && <><span>·</span><span className="truncate italic text-xs">{entry.note}</span></>}
                          {isOwn && (
                            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setEditingEntryId(entry.id)}
                                className="p-0.5 rounded hover:bg-muted"
                                title="Edit time entry"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Delete this time entry? This cannot be undone.')) return
                                  try {
                                    await timeEntryService.delete(entry.id)
                                    queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(task.id)])
                                    toast.success('Time entry deleted')
                                  } catch {
                                    toast.error('Failed to delete time entry')
                                  }
                                }}
                                className="p-0.5 rounded hover:bg-destructive/10 text-destructive"
                                title="Delete time entry"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{task?.title}" and all its comments, attachments, and time entries.
              {task?.type?.hierarchy_level === 0 && task?.children?.length > 0 && ` Its ${task.children.length} sub-task(s) will be deleted too.`}
              {task?.type?.hierarchy_level === 1 && task?.children?.length > 0 && ` Its ${task.children.length} task(s) stay, without an epic.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProjectLayout>
  )
}

export default TaskPage
